import * as React from "react";
import { useSelector } from "react-redux";
import { LocationPros } from "../../../../../types/baseTypes";
import { RootState } from "../../../../../redux/store";
import {
  setHasSelectedLocation,
  setSelectedLocation,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { setSelectedLocation as setSubscriptionSelectedLocation } from "../../../../../redux/slice/subscription/subscriptionSlice";
import { useTranslation } from "react-i18next";
import { cleanLocationName } from "../../../../../util/locationNameUtils";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "components/ui/select";
import { useAppDispatch } from "../../../../../redux/useDispatch";
import { cn } from "../../../../../util/cn";
import { cityOf, townOf } from "../../../../../util/locationAddress";

interface FilteredSelectProps {
  locations: LocationPros[];
  triggerClassName?: string;
  placeholder?: string;
}


function FilteredSelect({ locations, triggerClassName, placeholder }: FilteredSelectProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  const isDisabled = locations.length === 0;
  const selectOptions = React.useMemo(() => {
    const sorted = [...locations].sort((a, b) => {
      const aName = cleanLocationName(a.name);
      const bName = cleanLocationName(b.name);
      const byName = aName.localeCompare(bName, "de", { sensitivity: "base", numeric: true });
      if (byName !== 0) return byName;
      // tie-breaker by city
      return cityOf(a).localeCompare(cityOf(b), "de", { sensitivity: "base", numeric: true });
    });
    return sorted.map((location) => ({
      value: location.id,
      label: [cleanLocationName(location.name), townOf(location)]
        .filter(Boolean)
        .join(", "),
    }));
  }, [locations]);

  const handleLocationChange = React.useCallback(
    (value: string) => {
      const found = locations.find((loc) => loc.id === value) || null;
      if (found) {
        dispatch(setSelectedLocation(found));
        dispatch(setSubscriptionSelectedLocation(found)); // Also store in subscription slice
        dispatch(setHasSelectedLocation(true));
      } else {
        dispatch(setSelectedLocation(null));
        dispatch(setSubscriptionSelectedLocation(null)); // Also clear in subscription slice
        dispatch(setHasSelectedLocation(false));
      }
    },
    [dispatch, locations]
  );

  React.useEffect(() => {
    dispatch(setHasSelectedLocation(!!selectedLocation));
  }, [dispatch, selectedLocation]);

  return (
    <Select value={selectedLocation?.id ?? ""} onValueChange={handleLocationChange} disabled={isDisabled}>
      <SelectTrigger
        aria-label={t("book_location.location")}
        className={cn("items-start h-auto py-3", triggerClassName)}
        wrapText
      >
        <SelectValue placeholder={placeholder ?? t("book_location.location")} />
      </SelectTrigger>
      <SelectContent>
        {selectOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            <span className="block max-w-full whitespace-normal break-words leading-snug">
              {opt.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default React.memo(FilteredSelect);